import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Form, Icon, Input, Message, Modal } from 'semantic-ui-react';

import Button from '../../../components/Button';
import { AddressService } from '../../../services/address';
import { maskCpfOrCnpj, maskPhoneNumber, maskZipCode } from '../../../services/masks';
import { UFOptions } from '../../bond-insurance-budget-form/types';
import { CreateModalContent, CreateModalHeader, CreateSectionTitle } from '../styles';

export type CreateCondominiumFormValues = {
  estateId: string;
  zipcode: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  number: string;
  complement: string;
  condominium: {
    document: string;
    legalName: string;
    type: string;
    units: string;
    assessor: string;
    phone: string;
    email: string;
    syndic: {
      name: string;
      document: string;
      rg: string;
      birthDate: string;
      email: string;
      phone: string;
    };
  };
};

type Props = {
  open: boolean;
  userId?: string;
  showEstateField: boolean;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  initialEstateId?: string;
  isSubmitting: boolean;
  submitError: string | null;
  isBackofficeTest: boolean;
  onClose: () => void;
  onSubmit: (values: CreateCondominiumFormValues) => Promise<void>;
};

type FormErrors = Record<string, string | undefined>;

const condominiumTypeOptions = [
  { key: 'RESIDENCIAL', value: 'RESIDENCIAL', text: 'Residencial' },
  { key: 'COMERCIAL', value: 'COMERCIAL', text: 'Comercial' },
  { key: 'MISTO', value: 'MISTO', text: 'Misto' }
];

const condominiumPropertyTypeOptions = [
  { key: 'CONDOMINIO', value: 'CONDOMINIO', text: 'Condomínio' }
];

const initialValues: CreateCondominiumFormValues = {
  estateId: '',
  zipcode: '',
  street: '',
  neighborhood: '',
  city: '',
  state: '',
  number: '',
  complement: '',
  condominium: {
    document: '',
    legalName: '',
    type: '',
    units: '',
    assessor: '',
    phone: '',
    email: '',
    syndic: {
      name: '',
      document: '',
      rg: '',
      birthDate: '',
      email: '',
      phone: ''
    }
  }
};

const requiredAddressFields = [
  'zipcode',
  'street',
  'neighborhood',
  'city',
  'state',
  'number'
] as const;

const requiredCondominiumFields = [
  'condominium.document',
  'condominium.legalName',
  'condominium.type',
  'condominium.syndic.name',
  'condominium.syndic.email',
  'condominium.syndic.phone'
] as const;

const getValue = (values: CreateCondominiumFormValues, path: string): string => {
  const parts = path.split('.');
  let current: any = values;

  parts.forEach(part => {
    current = current?.[part];
  });

  return String(current || '');
};

const CreateCondominiumModal: React.FC<Props> = ({
  open,
  userId,
  showEstateField,
  estateOptions,
  initialEstateId,
  isSubmitting,
  submitError,
  isBackofficeTest,
  onClose,
  onSubmit
}) => {
  const [values, setValues] = useState<CreateCondominiumFormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [zipLookupError, setZipLookupError] = useState<string | null>(null);
  const lastFetchedZipRef = useRef('');

  useEffect(() => {
    if (!open) return;
    setValues({ ...initialValues, estateId: initialEstateId || '' });
    setErrors({});
    setZipLookupError(null);
    lastFetchedZipRef.current = '';
  }, [initialEstateId, open]);

  const updateField = (field: string, value: string) => {
    setValues(previousValues => {
      const nextValues: any = { ...previousValues };
      const parts = field.split('.');
      let current = nextValues;

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          current[part] = value;
          return;
        }
        current[part] = { ...current[part] };
        current = current[part];
      });

      return nextValues;
    });
    setErrors(previousErrors => ({ ...previousErrors, [field]: undefined }));
    if (field === 'zipcode') setZipLookupError(null);
  };

  const getRequiredError = (field: string): string | undefined => {
    const value = getValue(values, field).trim();
    if (!value) return 'Campo obrigatório.';
    if (field === 'zipcode' && value.replace(/\D/g, '').length !== 8) {
      return 'Informe um CEP válido.';
    }
    return undefined;
  };

  const validateRequiredField = (field: string) => {
    setErrors(previousErrors => ({
      ...previousErrors,
      [field]: getRequiredError(field)
    }));
  };

  const lookupZipCode = async (zipcode: string) => {
    const digits = zipcode.replace(/\D/g, '');
    if (digits.length !== 8 || lastFetchedZipRef.current === digits) return;

    lastFetchedZipRef.current = digits;
    try {
      setZipLookupError(null);
      setIsZipLoading(true);
      const address = await AddressService.getAddressByZipCode(digits, userId);
      setValues(previousValues => ({
        ...previousValues,
        zipcode: maskZipCode(digits),
        street: address.street || previousValues.street,
        neighborhood: address.district || previousValues.neighborhood,
        city: address.city || previousValues.city,
        state: (address.state || previousValues.state || '').toUpperCase()
      }));
      setErrors(previousErrors => ({
        ...previousErrors,
        zipcode: undefined,
        street: address.street ? undefined : previousErrors.street,
        neighborhood: address.district ? undefined : previousErrors.neighborhood,
        city: address.city ? undefined : previousErrors.city,
        state: address.state ? undefined : previousErrors.state
      }));
    } catch (_error) {
      lastFetchedZipRef.current = '';
      setZipLookupError('CEP não encontrado. Preencha o endereço manualmente.');
    } finally {
      setIsZipLoading(false);
    }
  };

  const handleZipCodeChange = (value: string) => {
    const maskedValue = maskZipCode(value || '');
    const digits = maskedValue.replace(/\D/g, '');
    updateField('zipcode', maskedValue);
    if (digits.length < 8) lastFetchedZipRef.current = '';
    if (digits.length === 8) lookupZipCode(maskedValue);
  };

  const validate = (): FormErrors => {
    const validationErrors: FormErrors = {};

    if (showEstateField && !values.estateId.trim()) {
      validationErrors.estateId = 'Campo obrigatório.';
    }

    requiredAddressFields.forEach(field => {
      const error = getRequiredError(field);
      if (error) validationErrors[field] = error;
    });

    requiredCondominiumFields.forEach(field => {
      const error = getRequiredError(field);
      if (error) validationErrors[field] = error;
    });

    if (values.condominium.units.trim()) {
      const units = Number(values.condominium.units);
      if (Number.isNaN(units) || units < 0 || !Number.isInteger(units)) {
        validationErrors['condominium.units'] = 'Informe um número inteiro válido.';
      }
    }

    return validationErrors;
  };

  const renderError = (field: string) =>
    errors[field] ? <small style={{ color: '#9f3a38' }}>{errors[field]}</small> : null;

  const handleSubmit = async () => {
    if (isBackofficeTest) return;

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    try {
      await onSubmit(values);
    } catch (_error) {
      // O erro da API é exibido na própria modal pelo componente pai.
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <CreateModalHeader>
          <Icon name="building" size="small" />
          Detalhes do imóvel
        </CreateModalHeader>
      </Modal.Header>

      <CreateModalContent>
        {submitError && <Message negative content={submitError} />}
        {zipLookupError && <Message warning content={zipLookupError} />}

        <Form>
          {showEstateField && (
            <Form.Group widths={3}>
              <Form.Field required error={Boolean(errors.estateId)}>
                <label>Imobiliária</label>
                <Dropdown
                  fluid
                  search
                  selection
                  placeholder="Selecione"
                  options={estateOptions}
                  value={values.estateId}
                  noResultsMessage="Nenhuma imobiliária encontrada."
                  onChange={(_, data) =>
                    updateField('estateId', String(data.value || ''))
                  }
                  onBlur={() => validateRequiredField('estateId')}
                />
                {renderError('estateId')}
              </Form.Field>
            </Form.Group>
          )}

          <CreateSectionTitle>Endereço do imóvel</CreateSectionTitle>

          <Form.Group widths="equal">
            <Form.Field required>
              <label>Tipo do imóvel</label>
              <Dropdown
                fluid
                selection
                options={condominiumPropertyTypeOptions}
                value="CONDOMINIO"
                disabled
              />
            </Form.Field>
            <Form.Field required error={Boolean(errors.zipcode)}>
              <label>CEP</label>
              <Input
                placeholder="Digite..."
                value={values.zipcode}
                loading={isZipLoading}
                maxLength={9}
                onChange={(_, data) => handleZipCodeChange(String(data.value || ''))}
                onBlur={() => validateRequiredField('zipcode')}
              />
              {renderError('zipcode')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.street)}>
              <label>Logradouro</label>
              <Input
                placeholder="Digite..."
                value={values.street}
                onChange={(_, data) => updateField('street', String(data.value || ''))}
                onBlur={() => validateRequiredField('street')}
              />
              {renderError('street')}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors.neighborhood)}>
              <label>Bairro</label>
              <Input
                placeholder="Digite..."
                value={values.neighborhood}
                onChange={(_, data) =>
                  updateField('neighborhood', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('neighborhood')}
              />
              {renderError('neighborhood')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.city)}>
              <label>Cidade</label>
              <Input
                placeholder="Digite..."
                value={values.city}
                onChange={(_, data) => updateField('city', String(data.value || ''))}
                onBlur={() => validateRequiredField('city')}
              />
              {renderError('city')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.state)}>
              <label>UF</label>
              <Dropdown
                fluid
                search
                selection
                placeholder="Selecione"
                options={UFOptions}
                value={values.state}
                onChange={(_, data) => updateField('state', String(data.value || ''))}
                onBlur={() => validateRequiredField('state')}
              />
              {renderError('state')}
            </Form.Field>
            <Form.Field required error={Boolean(errors.number)}>
              <label>Número</label>
              <Input
                placeholder="Digite..."
                value={values.number}
                onChange={(_, data) => updateField('number', String(data.value || ''))}
                onBlur={() => validateRequiredField('number')}
              />
              {renderError('number')}
            </Form.Field>
          </Form.Group>

          <Form.Group widths={3}>
            <Form.Field>
              <label>Complemento</label>
              <Input
                placeholder="Digite..."
                value={values.complement}
                onChange={(_, data) =>
                  updateField('complement', String(data.value || ''))
                }
              />
            </Form.Field>
          </Form.Group>

          <CreateSectionTitle>Informações do Condomínio</CreateSectionTitle>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors['condominium.document'])}>
              <label>CPF/CNPJ</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.document}
                onChange={(_, data) =>
                  updateField(
                    'condominium.document',
                    maskCpfOrCnpj(String(data.value || ''))
                  )
                }
                onBlur={() => validateRequiredField('condominium.document')}
              />
              {renderError('condominium.document')}
            </Form.Field>
            <Form.Field required error={Boolean(errors['condominium.legalName'])}>
              <label>Razão social</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.legalName}
                onChange={(_, data) =>
                  updateField('condominium.legalName', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('condominium.legalName')}
              />
              {renderError('condominium.legalName')}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors['condominium.type'])}>
              <label>Tipo</label>
              <Dropdown
                fluid
                selection
                placeholder="Selecione"
                options={condominiumTypeOptions}
                value={values.condominium.type}
                onChange={(_, data) =>
                  updateField('condominium.type', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('condominium.type')}
              />
              {renderError('condominium.type')}
            </Form.Field>
            <Form.Field error={Boolean(errors['condominium.units'])}>
              <label>Unidades</label>
              <Input
                type="number"
                min={0}
                placeholder="Digite..."
                value={values.condominium.units}
                onChange={(_, data) =>
                  updateField('condominium.units', String(data.value || ''))
                }
              />
              {renderError('condominium.units')}
            </Form.Field>
            <Form.Field>
              <label>Assessor</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.assessor}
                onChange={(_, data) =>
                  updateField('condominium.assessor', String(data.value || ''))
                }
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths={2}>
            <Form.Field>
              <label>Telefone do condomínio</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.phone}
                onChange={(_, data) =>
                  updateField(
                    'condominium.phone',
                    maskPhoneNumber(String(data.value || ''))
                  )
                }
              />
            </Form.Field>
            <Form.Field>
              <label>E-mail do condomínio</label>
              <Input
                type="email"
                placeholder="Digite..."
                value={values.condominium.email}
                onChange={(_, data) =>
                  updateField('condominium.email', String(data.value || ''))
                }
              />
            </Form.Field>
          </Form.Group>

          <CreateSectionTitle>Informações do Síndico</CreateSectionTitle>

          <Form.Group widths="equal">
            <Form.Field required error={Boolean(errors['condominium.syndic.name'])}>
              <label>Nome do Síndico</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.syndic.name}
                onChange={(_, data) =>
                  updateField('condominium.syndic.name', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('condominium.syndic.name')}
              />
              {renderError('condominium.syndic.name')}
            </Form.Field>
            <Form.Field>
              <label>CPF/CNPJ</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.syndic.document}
                onChange={(_, data) =>
                  updateField(
                    'condominium.syndic.document',
                    maskCpfOrCnpj(String(data.value || ''))
                  )
                }
              />
            </Form.Field>
            <Form.Field>
              <label>RG</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.syndic.rg}
                onChange={(_, data) =>
                  updateField('condominium.syndic.rg', String(data.value || ''))
                }
              />
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Data de Nascimento</label>
              <Input
                type="date"
                value={values.condominium.syndic.birthDate}
                onChange={(_, data) =>
                  updateField('condominium.syndic.birthDate', String(data.value || ''))
                }
              />
            </Form.Field>
            <Form.Field required error={Boolean(errors['condominium.syndic.email'])}>
              <label>E-mail</label>
              <Input
                type="email"
                placeholder="Digite..."
                value={values.condominium.syndic.email}
                onChange={(_, data) =>
                  updateField('condominium.syndic.email', String(data.value || ''))
                }
                onBlur={() => validateRequiredField('condominium.syndic.email')}
              />
              {renderError('condominium.syndic.email')}
            </Form.Field>
            <Form.Field required error={Boolean(errors['condominium.syndic.phone'])}>
              <label>Telefone</label>
              <Input
                placeholder="Digite..."
                value={values.condominium.syndic.phone}
                onChange={(_, data) =>
                  updateField(
                    'condominium.syndic.phone',
                    maskPhoneNumber(String(data.value || ''))
                  )
                }
                onBlur={() => validateRequiredField('condominium.syndic.phone')}
              />
              {renderError('condominium.syndic.phone')}
            </Form.Field>
          </Form.Group>
        </Form>
      </CreateModalContent>

      <Modal.Actions>
        <Button
          light
          type="button"
          text="Cancelar"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <Button
          color="green"
          type="button"
          text="Criar imóvel"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || isBackofficeTest}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default CreateCondominiumModal;
